import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Copy, Pencil, Trash2, Plus } from "lucide-react";

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", permissions: [], parent_role: "" });

  const load = async () => {
    const [r, c] = await Promise.all([api.get("/roles"), api.get("/permissions/catalog")]);
    setRoles(r.data);
    setCatalog(c.data.catalog);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", description: "", permissions: [], parent_role: "" });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name, description: r.description || "", permissions: [...(r.permissions || [])], parent_role: r.parent_role || "" });
    setOpen(true);
  };
  const togglePerm = (key) => {
    setForm((f) => f.permissions.includes(key)
      ? { ...f, permissions: f.permissions.filter((p) => p !== key) }
      : { ...f, permissions: [...f.permissions, key] });
  };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/roles/${editing.id}`, { description: form.description, permissions: form.permissions, parent_role: form.parent_role || null });
        toast.success("Role updated");
      } else {
        await api.post("/roles", { name: form.name, description: form.description, permissions: form.permissions, parent_role: form.parent_role || null });
        toast.success("Role created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const clone = async (r) => {
    try {
      await api.post(`/roles/${r.id}/clone`);
      toast.success(`Cloned ${r.name}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete role ${r.name}?`)) return;
    try {
      await api.delete(`/roles/${r.id}`);
      toast.success("Role deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="roles-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">Roles & permissions</h1>
          <p className="text-muted-foreground">Create custom roles, assign module-level permissions, clone existing roles and build role hierarchies.</p>
        </div>
        <Button onClick={openCreate} data-testid="create-role-btn"><Plus className="w-4 h-4 mr-1" /> New role</Button>
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Inherits</TableHead>
              <TableHead>Permissions</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.map((r) => (
              <TableRow key={r.id} data-testid={`role-row-${r.name}`}>
                <TableCell className="font-mono text-xs font-semibold">{r.name}</TableCell>
                <TableCell>{r.is_system
                  ? <Badge variant="outline">System</Badge>
                  : <Badge>Custom</Badge>}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{r.description}</TableCell>
                <TableCell className="font-mono text-[10px]">{r.parent_role || "—"}</TableCell>
                <TableCell className="text-xs">{(r.permissions || []).includes("*") ? <Badge>Everything</Badge> : `${(r.permissions || []).length} perms`}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => clone(r)} data-testid={`clone-${r.name}`} title="Clone"><Copy className="w-4 h-4" /></Button>
                  {!r.is_system && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-${r.name}`} title="Edit"><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(r)} data-testid={`delete-${r.name}`} title="Delete"><Trash2 className="w-4 h-4" /></Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit role — ${editing.name}` : "Create a new role"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!editing && (
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. WAREHOUSE_LEAD" data-testid="role-name" />
              </div>
            )}
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="role-desc" />
            </div>
            <div>
              <Label>Inherits from (hierarchy)</Label>
              <Select value={form.parent_role || "__none"} onValueChange={(v) => setForm({ ...form, parent_role: v === "__none" ? "" : v })}>
                <SelectTrigger data-testid="role-parent"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">None</SelectItem>
                  {roles.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">Child role inherits parent permissions; approval cascade flows to parent role.</p>
            </div>

            <div>
              <Label>Permissions (module · action)</Label>
              <div className="max-h-72 overflow-y-auto border rounded-md p-3 space-y-3 mt-1">
                {Object.entries(catalog).map(([mod, acts]) => (
                  <div key={mod}>
                    <div className="font-semibold text-sm capitalize mb-1">{mod}</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {acts.map((a) => {
                        const k = `${mod}.${a}`;
                        return (
                          <label key={k} className="flex items-center gap-2 text-xs">
                            <Checkbox checked={form.permissions.includes(k)} onCheckedChange={() => togglePerm(k)} data-testid={`perm-${k}`} />
                            {a}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-role">{editing ? "Save changes" : "Create role"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
